import { generateClient } from "aws-amplify/api";
import { createPoBox, updatePoBox } from "graphql/customs/mutations";
import { poBoxByUser, poBoxesByDate, getPoBox as fetchPoBox, listPoBoxes } from "graphql/queries";
import { cleanUpData, errorHandler, getDataFromResponse, getNotDeletedItems } from "utils/Api";
import { REFERAL_AMOUNT_BENEFITS, REFERAL_USER_COOKIE_NAME, SEQUENCETYPES } from "utils/Constants";
import { poBoxLabelGenerator, sequenceGenerator } from "utils/Form";
import { getTypeCounterByName, increaseTypeCounter } from "./TypeCounterApi";
import { Cookies } from 'react-cookie';
import sortArray from 'sort-array';
import { savePoBoxReferal } from "./PoBoxReferalApi";
import { savePoBoxReferalPoint } from "./PoBoxReferalPointAPI";
import { createWarehouseClient } from "./WarehouseClientApi";

const API = generateClient();

export const savePoBox = async (data) => {
    const operationName = (data && data.id) ? "update" : "create";
    const operation = operationName === "update" ? updatePoBox : createPoBox;
    let cleanData = cleanUpData(data);
    cleanData["type"] = "PoBox";
    cleanData["name"] = (cleanData["name"] || "").toLowerCase() // normalize name for search
    cleanData["email"] = (cleanData["email"] || "").toLowerCase() // normalize name for search

    delete cleanData["addresses"];
    delete cleanData["boxes"];

    let counter;
    if (operationName === "create") {
        counter = await getTypeCounterByName(SEQUENCETYPES.PO_BOX);
        const sequenceNumber = sequenceGenerator(SEQUENCETYPES.PO_BOX, counter);
        cleanData = {
            ...cleanData,
            boxNumber: sequenceNumber
        };
    }

    const saveResponse = await API.graphql({
        query: operation,
        variables: {
            "input": cleanData
        }
    });

    if (operationName === "create") {
        await increaseTypeCounter(counter);
    }
    return getDataFromResponse(saveResponse, operationName === "create" ? "createPoBox" : "updatePoBox");
};

export const getPoBoxes = async (filter = {}) => {
    const data = await API.graphql({
        query: listPoBoxes,
        variables: {
            "filter": filter
        }
    })
    return getNotDeletedItems(data, "listPoBoxes");
}

export const getPoBox = async (poBoxId) => {
    let poBoxObject = await API.graphql({
        query: fetchPoBox,
        variables: {
            id: poBoxId
        }
    });

    let poBox = getDataFromResponse(poBoxObject, "getPoBox");
    if (poBox && poBox.boxes && poBox.boxes.items) {
        poBox.boxes.items = sortArray(poBox.boxes.items, {
            by: 'createdAt',
            order: 'desc'
        })
    }
    return poBox;
}

export const getUserPoBox = async (poBoxId) => {
    let poBoxObject = await API.graphql({
        query: fetchPoBox,
        variables: {
            id: poBoxId
        }
    });

    let poBox = getDataFromResponse(poBoxObject, "getPoBox");
    if (poBox && poBox.boxes && poBox.boxes.items) {
        poBox.boxes.items = sortArray(poBox.boxes.items, {
            by: 'createdAt',
            order: 'desc'
        })
    }
    return poBox;
}

const createReferalEntry = async (fromPoBoxNumber, toPoBoxId) => {
    try {
        const fromPoBox = await getPoBoxByNumber(fromPoBoxNumber);
        await savePoBoxReferal({
            poBoxReferalReferFromPoBoxId: fromPoBox.id,
            poBoxReferalReferToPoBoxId: toPoBoxId
        });

        const referalPointEntry = {
            poBoxReferalPointReferalPoBoxId: toPoBoxId,
            amount: REFERAL_AMOUNT_BENEFITS,
            description: `Se asignaron $${REFERAL_AMOUNT_BENEFITS} en créditos por la referencia de tu amigo ${fromPoBox.name} => ${poBoxLabelGenerator(fromPoBox)}`
        };
        await savePoBoxReferalPoint(referalPointEntry);
    } catch (e) {
        errorHandler(e, "Create Referal Entry");
    }
}

export const createPOBoxIfNeeded = async (userData) => {
    let poBoxObject = await API.graphql({
        query: poBoxByUser,
        variables: {
            userId: userData.id
        }
    });
    const poBox = getNotDeletedItems(poBoxObject, "poBoxByUser");

    if (poBox.length === 0) {
        const newPoBox = await savePoBox({
            userId: userData.id,
            name: userData.attributes?.name,
            email: userData.attributes?.email,
            phone: userData.attributes["custom:tel_mobile"]
        });

        const cookies = new Cookies();
        const referalBox = cookies.get(REFERAL_USER_COOKIE_NAME);
        if (referalBox) {
            await createReferalEntry(referalBox, newPoBox?.id);
        }
        await createWarehouseClient(newPoBox)
        return newPoBox;
    }
}


export const getPoBoxesByDate = async ({
    sortDirection = "DESC",
    limit = 50,
    nextToken = null,
    filter = null,
} = {}) => {

    const response = await API.graphql({
        query: poBoxesByDate,
        variables: {
            type: "PoBox",
            sortDirection,
            limit,
            nextToken,
            filter,
        },
    });

    const result = response?.data?.poBoxesByDate || {};
    const items = getNotDeletedItems(response, "poBoxesByDate");

    return {
        items: items || [],
        nextToken: result.nextToken || null,
    };
}

export const getPOBoxByUser = async (userData) => {
    let poBoxObject = await API.graphql({
        query: poBoxByUser,
        variables: {
            userId: userData.id
        }
    });
    const poBox = getNotDeletedItems(poBoxObject, "poBoxByUser");
    if (poBox.length > 0) {
        return await getUserPoBox(poBox[0].id);
    }
    return null;
}

export const getPoBoxByNumber = async (poBoxNumber = "") => {
    const data = await API.graphql({
        query: listPoBoxes,
        variables: {
            "filter": {
                "boxNumber": {
                    "eq": poBoxNumber
                }
            }
        }
    })
    const poBox = getNotDeletedItems(data, "listPoBoxes");
    if (poBox.length !== 0) {
        return poBox[0];
    }
    return null;
}